import { TRecData, TRejectReasons } from 'types/dist/common.types';
import { z } from 'zod';
import type { Role } from '@huddle01/server-sdk/auth';

// Can be used in future to restrict wallets based on chain
// const allowedChains = ['ethereum', 'tezos', 'cosmos', 'solana', "etc"];

// keys are the names of the connectors from wallet01, values are the names of the wallets for iframe
export const AllowedWallets = {
  all: '*',
  injected: 'metamask',
  walletconnect: 'walletconnect',
  keplr: 'keplr',
  templewallet: 'templewallet',
  phantom: 'phantom',
  beacon: 'beacon',
  okxwallet: 'okxwallet',
  suku: 'suku',
  // or
  lens: 'lens',
  ud: 'ud',
  cyberconnect: 'cyberconnect',
} as const;

export const reactions = [
  '😂',
  '😢',
  '😦',
  '😍',
  '🤔',
  '👀',
  '🙌',
  '👍',
  '👎',
  '🔥',
  '🍻',
  '🚀',
  '🎉',
  '❤️',
  '💯',
] as const;

export type TReaction = (typeof reactions)[number];

export const AllowedWalletsSchema = z.union([
  z.literal('walletconnect'),
  z.literal('keplr'),
  z.literal('templewallet'),
  z.literal('phantom'),
  z.literal('beacon'),
  z.literal('okxwallet'),
  z.literal('suku'),
  z.literal('lens'),
  z.literal('ud'),
  z.literal('cyberconnect'),
  z.literal('*'),
  z.literal('metamask'),
]);

export const ClientConfigSchema = z.object({
  hostName: z.boolean().optional(), // private
  iframeEmbed: z.boolean().optional(), // private
  logoUrl: z.string().optional(),
  logoHeight: z.number().optional(),
  logoWidth: z.number().optional(),
  background: z.string().optional(),
  defaultChainId: z.number().optional(),
  redirectUrlOnLeave: z.string().optional(),
  wallets: z.array(AllowedWalletsSchema).default(['*']),
  virtualBgs: z.array(z.string()).optional(),
  walletAddress: z.string().optional(),
});

// export const ClientConfigSchema = z.object({
//   logoUrl: z.string().optional(),
//   background: z.string().optional(),
//   redirectUrlOnLeave: z.string().optional(),
//   wallets: z.array(z.enum(['*', ...Object.values(AllowedWallets)])).optional(),
//   lobbyLoginMessage: z.string().optional(),
//   lobbyTitle: z.string().optional(),
//   lobbyDescription: z.string().optional(),
//   isCopyMeetingVisible: z.boolean().optional(),
// });

export const RecordingDataSchema = z.object({
  size: z.number(),
  duration: z.number(),
  s3URL: z.string(),
  ipfsURL: z.string(),
  uploadToIpfs: z.boolean().optional(),
});

export type TRecordingData = z.infer<typeof RecordingDataSchema>;

type TPreviewPeers = {
  displayName: string;
  walletAddress: string | undefined;
}[];

export type THuddleEventsMap = {
  'app:initialized': [];
  'lobby:metadata': [TPreviewPeers];
  'room:joined': [];
  'room:failed': [{ reason: TRejectReasons }];
  'room:new-peer': [
    {
      peerId: string;
      displayName: string;
      avatarUrl: string;
      role: Role;
    },
  ];
  'room:me-left': [];
  'room:peer-left': [{ peerId: string }];
  'room:recording-started': [];
  'room:recording-stopping': [];
  'room:recording-stopped': [TRecordingData];
  'room:recording-data': [TRecData];
  'room:livestream-started': [];
  'room:livestream-stopped': [];
};

export type THuddleEvents = keyof THuddleEventsMap;

export type TClientConfig = Partial<z.infer<typeof ClientConfigSchema>>;

export interface IColorScheme {
  iconColor: string;
  textColor: string;
  borderColor: string;
  brandColor: string;
  interfaceColor: string;
  onBrandColor: string;
  backgroundColor: string;
  accentColor: string;
}

export const darkTheme: IColorScheme = {
  iconColor: '#94A3B8',
  textColor: '#f8fafc',
  borderColor: '#1C1E24',
  brandColor: '#246BFD',
  interfaceColor: '#181A20',
  onBrandColor: '#ffffff',
  backgroundColor: '#121214',
  accentColor: '#334155',
} as const;

export const lightTheme: IColorScheme = {
  iconColor: '#64748B',
  textColor: '#475569',
  borderColor: '#CBD5E1',
  brandColor: '#246BFD',
  interfaceColor: '#F1F5F9',
  onBrandColor: '#ffffff',
  backgroundColor: '#ffffff',
  accentColor: '#CBD5E1',
} as const;
